import { undefinedOrTransformToDTOCurried } from '@maia/utils';
import { ICustomerEntity } from '../shared';
import { AddressPostDto } from './common/address.dto';
import { TagDto } from '@maia/tags';
import { BillingUnitDto } from './common/billing-unit.dto';

export class CustomerDto {
  readonly guid: string;
  readonly activeSince: Date;
  readonly status: ReturnType<typeof TagDto.getCustomerStatusTag>;
  readonly reference: string;
  readonly type: 'organization' | 'consumer';
  readonly iban: string;
  readonly billingUnit: BillingUnitDto;
  readonly address: {
    readonly guid: string;
    readonly street: string;
    readonly number: number;
    readonly appendix: string | undefined;
    readonly city: string;
    readonly postalCode: string;
    readonly country: string;
  };
  readonly organization: unknown;
  readonly consumer: {
    readonly initials: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly fullName: string;
    readonly email: string;
    readonly phone: string | undefined;
    readonly dateOfBirth: Date;
  };
  readonly isDeletable: boolean;

  constructor(customer: ICustomerEntity) {
    this.guid = customer.guid;
    this.activeSince = new Date(customer.active_since);
    this.status = TagDto.getCustomerStatusTag(customer.status);
    this.reference = customer.reference;
    this.type = customer.type;
    this.iban = customer.iban;
    this.billingUnit = new BillingUnitDto(customer.billing_unit);
    this.address = {
      guid: customer.address.guid,
      street: customer.address.street,
      number: customer.address.house_number,
      appendix: customer.address.house_number_appendix ?? undefined,
      city: customer.address.city,
      postalCode: customer.address.postal_code,
      country: customer.address.country,
    };
    this.consumer = {
      initials: customer.consumer.initials,
      firstName: customer.consumer.first_name,
      lastName: customer.consumer.last_name,
      fullName: customer.consumer.full_name,
      email: customer.consumer.email,
      phone: customer.consumer.phone ?? undefined,
      dateOfBirth: new Date(customer.consumer.date_of_birth),
    };
    this.isDeletable = customer.is_deletable;
  }
}

export const undefinedOrCustomer = undefinedOrTransformToDTOCurried(
  (entity: ICustomerEntity) => new CustomerDto(entity),
);

export class CustomerCreateDto {
  readonly type: 'consumer';
  readonly initials: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly email: string;
  readonly date_of_birth: string;
  readonly phone: string | undefined;
  readonly address: AddressPostDto;

  constructor(params: {
    type: 'consumer';
    initials: string;
    firstname: string;
    lastname: string;
    email: string;
    birthdate: string;
    phone: string | undefined;
    address: ConstructorParameters<typeof AddressPostDto>[0];
  }) {
    this.type = params.type;
    this.initials = params.initials;
    this.first_name = params.firstname;
    this.last_name = params.lastname;
    this.email = params.email;
    this.date_of_birth = params.birthdate;
    this.phone = params.phone;
    this.address = new AddressPostDto(params.address);
  }
}

export class CustomerUpdateDto {
  readonly type: 'consumer';
  readonly initials: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly email: string;
  readonly date_of_birth: string;
  readonly phone: string | undefined;
  readonly address: AddressPostDto;

  constructor(params: {
    type: 'consumer';
    initials: string;
    firstname: string;
    lastname: string;
    email: string;
    birthdate: string;
    phone: string | undefined;
    address: ConstructorParameters<typeof AddressPostDto>[0];
  }) {
    this.type = params.type;
    this.initials = params.initials;
    this.first_name = params.firstname;
    this.last_name = params.lastname;
    this.email = params.email;
    this.date_of_birth = params.birthdate;
    this.phone = params.phone;
    this.address = new AddressPostDto(params.address);
  }
}
